import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import {
  ChangeEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useId,
  useRef,
  useState,
} from 'react';
import usePrivacyContext from 'src/hooks/privacy/usePrivacyContext';
import { PrivacyDocument, PrivacyTemplate } from 'src/contexts/privacy/PrivacyContext';
import useWindowDimensions from 'src/hooks/useWindowDimensionHook';
import Page from 'src/components/Page';
import BoxTitlePage from 'src/theme/overrides/BoxTitlePage';
import { HEADER } from 'src/config';
import { ScrollContainer } from 'src/theme/scrollbar';

// ----------------------------------------------------------------------

type Lang = 'IT' | 'EN';

const LANG_LABELS: Record<Lang, string> = {
  IT: 'Italiano',
  EN: 'English',
};

// Stesso grigio di sfondo usato in AppGrid, per omologare il colore delle righe
const ROW_BACKGROUND = '#edf0f8';
const ROW_BACKGROUND_HOVER = '#e2e6f2';

// Unico azzurro per tutte le chips di tipo documento, leggermente più scuro
// di quello che prima era riservato solo alla chip LIA (#E8F4FD / #1565C0)
const TYPE_CHIP_BG = '#CFE6FB';
const TYPE_CHIP_COLOR = '#0B4C86';

const DOCUMENT_NAME_MAX_LENGTH = 120;

// Etichetta della chip di tipo, omologata ovunque compaia (Documents e Templates):
// 'general' → 'DOC', 'informativa_x' → 'INFO X', altrimenti il type così com'è (LIA, DPIA, CUSTOM...)
function getTypeLabel(type: string): string {
  if (type === 'general') return 'DOC';
  return type.replace('informativa_', 'Info ').toUpperCase();
}

const SectionCard = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}));

// Omologato con l'header della griglia in AppGrid (.super-app-theme--header):
// backgroundColor: theme.palette.primary.main, color: '#fff'
const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const DocumentRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 3),
  gap: theme.spacing(2),
  backgroundColor: ROW_BACKGROUND,
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'background 0.15s',
  '&:last-child': { borderBottom: 'none' },
  '&:hover': { backgroundColor: ROW_BACKGROUND_HOVER },
}));

const TypeChip = styled(Chip)(() => ({
  backgroundColor: TYPE_CHIP_BG,
  color: TYPE_CHIP_COLOR,
  fontWeight: 600,
  fontSize: '0.7rem',
  height: 22,
  borderRadius: 6,
}));

// ----------------------------------------------------------------------
// FLAG ICONS — disegnate in SVG (non emoji, per evitare che su alcuni OS
// vengano mostrate come testo "IT"/"GB" invece che come bandiera)
// ----------------------------------------------------------------------
function ItalyFlagIcon({ size = 20 }: { size?: number }) {
  return (
    <Box
      component="svg"
      width={size}
      height={size * 0.7}
      viewBox="0 0 3 2"
      sx={{ display: 'block', borderRadius: '2px', boxShadow: '0 0 0 1px rgba(0,0,0,0.2)' }}
    >
      <rect width="1" height="2" x="0" fill="#009246" />
      <rect width="1" height="2" x="1" fill="#fff" />
      <rect width="1" height="2" x="2" fill="#ce2b37" />
    </Box>
  );
}

function UkFlagIcon({ size = 20 }: { size?: number }) {
  const clipId = useId();
  return (
    <Box
      component="svg"
      width={size}
      height={size * 0.7}
      viewBox="0 0 60 30"
      sx={{ display: 'block', borderRadius: '2px', boxShadow: '0 0 0 1px rgba(0,0,0,0.2)' }}
    >
      <clipPath id={clipId}>
        <rect width="60" height="30" />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
        <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </Box>
  );
}

function FlagIcon({ lang, size = 20 }: { lang: Lang; size?: number }) {
  return lang === 'IT' ? <ItalyFlagIcon size={size} /> : <UkFlagIcon size={size} />;
}

// ----------------------------------------------------------------------
// LANGUAGE PICKER ICON BUTTON
// Bottone (download o upload) che, se sono disponibili più lingue, apre un
// piccolo pannello con le bandiere selezionabili prima di eseguire l'azione.
// Se è disponibile una sola lingua, la usa direttamente senza chiedere.
// ----------------------------------------------------------------------
function LanguagePickerIconButton({
  icon,
  tooltip,
  languages,
  onSelect,
  disabled = false,
  color = 'default',
}: {
  icon: ReactNode;
  tooltip: string;
  languages: Lang[];
  onSelect: (lang: Lang) => void;
  disabled?: boolean;
  color?: 'default' | 'primary';
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: ReactMouseEvent<HTMLElement>) => {
    if (languages.length <= 1) {
      if (languages[0]) onSelect(languages[0]);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handlePick = (lang: Lang) => {
    onSelect(lang);
    handleClose();
  };

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            size="small"
            color={color}
            disabled={disabled || languages.length === 0}
            onClick={handleOpen}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { borderRadius: '10px', mt: 0.5, backgroundColor: '#EEF0F4' } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ p: 1 }}
        >
          {languages.map((lang) => (
            <Tooltip key={lang} title={LANG_LABELS[lang]}>
              <IconButton
                size="small"
                onClick={() => handlePick(lang)}
                sx={{
                  p: 0.5,
                  borderRadius: '6px',
                  backgroundColor: '#E1E4EA',
                  '&:hover': { backgroundColor: '#D2D6E0' },
                }}
              >
                <FlagIcon lang={lang} size={24} />
              </IconButton>
            </Tooltip>
          ))}
        </Stack>
      </Popover>
    </>
  );
}

// ----------------------------------------------------------------------
// DOCUMENT ROW — SuperUser (store manager)
// Nessuna variante di lingua nei dati: download/upload restano diretti.
// ----------------------------------------------------------------------
function SuperUserDocumentRow({ doc }: { doc: PrivacyDocument }) {
  const { onHandleDownloadDocument, onHandleUploadTemplate, uploadingTemplateId, uploadSuccess, uploadError } = usePrivacyContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUploadable =
    doc.type === 'LIA' ||
    doc.type === 'DPIA' ||
    doc.type === 'informativa_semplificata' ||
    doc.type === 'informativa_estesa' ||
    doc.type === 'custom';
  const isUploading = uploadingTemplateId === doc.id;
  const isSuccess = uploadSuccess === doc.id;
  const isError = uploadError === doc.id;

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onHandleUploadTemplate(doc.id, file);
    e.target.value = '';
  };

  return (
    <DocumentRow>
      <DescriptionOutlinedIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>{doc.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {doc.uploadedAt ? `Updated on ${doc.uploadedAt}` : 'Not yet uploaded'}
        </Typography>
      </Box>
      <TypeChip label={getTypeLabel(doc.type)} />
      {isSuccess && <Chip label="Uploaded!" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 6 }} />}
      {isError && <Chip label="Error" size="small" sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 6 }} />}
      <Tooltip title={doc.uploadedAt ? 'Download' : 'No file uploaded yet'}>
        <span>
          <IconButton size="small" disabled={doc.type === 'custom' && !doc.uploadedAt} onClick={() => onHandleDownloadDocument(doc)}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {isUploadable ? (
        <>
          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleFileChange} />
          <Tooltip title="Upload your completed document">
            <span>
              <Button
                size="small" variant="contained"
                startIcon={isUploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
                disabled={isUploading}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  px: 1.5,
                  backgroundColor: 'grey.500',
                  color: '#fff',
                  '&:hover': { backgroundColor: 'grey.600' },
                }}
              >
                {isUploading ? 'Uploading...' : 'Upload Now'}
              </Button>
            </span>
          </Tooltip>
        </>
      ) : (
        <Box sx={{ width: 112, flexShrink: 0 }} />
      )}
    </DocumentRow>
  );
}

// ----------------------------------------------------------------------
// ADD DOCUMENT ROW — SuperUser only
// Inline row to create a new custom document: name required, file optional.
// ----------------------------------------------------------------------
function AddDocumentRow({ onClose }: { onClose: () => void }) {
  const { onHandleCreateDocument } = usePrivacyContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = name.trim();
  const canSave = trimmedName.length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onHandleCreateDocument(trimmedName, file ?? undefined);
      onClose();
    } catch {
      setError('Document not created. Check your connection and try again.');
      setSaving(false);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
    e.target.value = '';
  };

  return (
    <DocumentRow sx={{ backgroundColor: '#fff', '&:hover': { backgroundColor: '#fff' } }}>
      <DescriptionOutlinedIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Document name"
          value={name}
          disabled={saving}
          error={!!error}
          helperText={error ?? undefined}
          inputProps={{ maxLength: DOCUMENT_NAME_MAX_LENGTH, 'aria-label': 'Document name' }}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape' && !saving) onClose();
          }}
        />
      </Box>

      {file && (
        <Chip
          label={file.name}
          size="small"
          onDelete={saving ? undefined : () => setFile(null)}
          sx={{ maxWidth: 180, fontSize: '0.7rem', height: 22, borderRadius: 6 }}
        />
      )}

      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleFileChange} />
      <Tooltip title="Attach file (optional)">
        <span>
          <IconButton size="small" disabled={saving} onClick={() => fileInputRef.current?.click()}>
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Save document">
        <span>
          <IconButton size="small" color="primary" disabled={!canSave} onClick={handleSave}>
            {saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Cancel">
        <span>
          <IconButton size="small" disabled={saving} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </DocumentRow>
  );
}

// ----------------------------------------------------------------------
// DOCUMENT ROW — Admin
// ----------------------------------------------------------------------
function AdminDocumentRow({ doc }: { doc: PrivacyDocument }) {
  const { onHandleDownloadDocument, onHandleUploadTemplate, uploadingTemplateId, uploadSuccess, uploadError } = usePrivacyContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUploading = uploadingTemplateId === doc.id;
  const isSuccess = uploadSuccess === doc.id;
  const isError = uploadError === doc.id;

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onHandleUploadTemplate(doc.id, file);
    e.target.value = '';
  };

  return (
    <DocumentRow>
      <DescriptionOutlinedIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>{doc.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {doc.uploadedAt ? `Updated on ${doc.uploadedAt}` : 'Not yet uploaded'}
        </Typography>
      </Box>
      <TypeChip label={getTypeLabel(doc.type)} />
      {isSuccess && <Chip label="Uploaded!" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 6 }} />}
      {isError && <Chip label="Error" size="small" sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 6 }} />}
      <Tooltip title="Download">
        <IconButton size="small" onClick={() => onHandleDownloadDocument(doc)}>
          <FileDownloadOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleFileChange} />
      <Tooltip title="Upload new version">
        <span>
          <IconButton size="small" color="primary" disabled={isUploading} onClick={() => fileInputRef.current?.click()}>
            {isUploading ? <CircularProgress size={16} /> : <UploadFileOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </DocumentRow>
  );
}

// ----------------------------------------------------------------------
// DOCUMENT ROW — Planner / Analyst
// General ToS: download only.
// Informativa (semplificata / estesa): download if available, disabled if not yet uploaded by HQ.
// ----------------------------------------------------------------------
function PlannerDocumentRow({ doc }: { doc: PrivacyDocument }) {
  const { onHandleDownloadDocument } = usePrivacyContext();
  const isInformativa = doc.type === 'informativa_semplificata' || doc.type === 'informativa_estesa';
  const isAvailable = !isInformativa || !!doc.uploadedAt;

  return (
    <DocumentRow>
      <DescriptionOutlinedIcon sx={{ color: isAvailable ? 'text.secondary' : 'text.disabled', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap color={isAvailable ? 'text.primary' : 'text.disabled'}>
          {doc.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {doc.uploadedAt ? `Updated on ${doc.uploadedAt}` : 'Not yet uploaded by headquarters'}
        </Typography>
      </Box>
      <TypeChip label={getTypeLabel(doc.type)} />
      {isAvailable ? (
        <Tooltip title="Download">
          <IconButton size="small" onClick={() => onHandleDownloadDocument(doc)}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Document not yet available">
          <span>
            <IconButton size="small" disabled>
              <FileDownloadOutlinedIcon fontSize="small" sx={{ opacity: 0.3 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </DocumentRow>
  );
}

// ----------------------------------------------------------------------
// TEMPLATES — un'unica riga per ciascun tipo (LIA, DPIA, Info Semplificata,
// Info Estesa): la lingua (IT/EN) viene chiesta al click su download/upload,
// invece che filtrata a monte tramite un selettore nell'header.
// ----------------------------------------------------------------------
type TemplateGroup = {
  type: PrivacyTemplate['type'];
  name: string;
  it?: PrivacyTemplate;
  en?: PrivacyTemplate;
};

const TEMPLATE_TYPE_ORDER: PrivacyTemplate['type'][] = ['LIA', 'DPIA', 'informativa_semplificata', 'informativa_estesa'];

const TEMPLATE_TYPE_NAMES: Record<PrivacyTemplate['type'], string> = {
  LIA: 'LIA Assessment Template',
  DPIA: 'DPIA Standard Template',
  informativa_semplificata: 'Informativa Semplificata',
  informativa_estesa: 'Informativa Estesa',
};

function buildTemplateGroups(templates: PrivacyTemplate[]): TemplateGroup[] {
  return TEMPLATE_TYPE_ORDER
    .map((type) => ({
      type,
      name: TEMPLATE_TYPE_NAMES[type],
      it: templates.find((t) => t.type === type && t.language === 'IT'),
      en: templates.find((t) => t.type === type && t.language === 'EN'),
    }))
    .filter((group) => group.it || group.en);
}

// TEMPLATE ROW — Admin / SuperUser
// canUpload=true for Admin only (SuperUser can only download templates)
function AdminTemplateGroupRow({ group, canUpload }: { group: TemplateGroup; canUpload: boolean }) {
  const { onHandleDownloadDocument, onHandleUploadTemplate, uploadingTemplateId, uploadSuccess, uploadError } = usePrivacyContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadLangRef = useRef<Lang | null>(null);

  const availableLanguages: Lang[] = [
    ...(group.it ? (['IT'] as Lang[]) : []),
    ...(group.en ? (['EN'] as Lang[]) : []),
  ];

  const groupIds = [group.it?.id, group.en?.id].filter(Boolean) as string[];
  const isUploading = groupIds.includes(uploadingTemplateId || '');
  const isSuccess = groupIds.includes(uploadSuccess || '');
  const isError = groupIds.includes(uploadError || '');

  const latestUpdatedAt = [group.it?.uploadedAt, group.en?.uploadedAt]
    .filter((d): d is string => !!d)
    .sort()
    .reverse()[0];

  const handleDownloadSelect = (lang: Lang) => {
    const target = lang === 'IT' ? group.it : group.en;
    if (target) onHandleDownloadDocument(target);
  };

  const handleUploadSelect = (lang: Lang) => {
    const target = lang === 'IT' ? group.it : group.en;
    if (!target) return;
    pendingUploadLangRef.current = lang;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const lang = pendingUploadLangRef.current;
    const target = lang === 'IT' ? group.it : lang === 'EN' ? group.en : undefined;
    if (file && target) onHandleUploadTemplate(target.id, file);
    e.target.value = '';
  };

  return (
    <DocumentRow>
      <DescriptionOutlinedIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>{group.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {latestUpdatedAt ? `Updated on ${latestUpdatedAt}` : 'Not yet uploaded'}
        </Typography>
      </Box>
      <TypeChip label={getTypeLabel(group.type)} />
      {isSuccess && <Chip label="Uploaded!" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 6 }} />}
      {isError && <Chip label="Error" size="small" sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 6 }} />}

      <LanguagePickerIconButton
        icon={<FileDownloadOutlinedIcon fontSize="small" />}
        tooltip="Download template"
        languages={availableLanguages}
        onSelect={handleDownloadSelect}
      />

      {canUpload && (
        <>
          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleFileChange} />
          <LanguagePickerIconButton
            icon={isUploading ? <CircularProgress size={16} /> : <UploadFileOutlinedIcon fontSize="small" />}
            tooltip="Upload new version"
            languages={availableLanguages}
            onSelect={handleUploadSelect}
            disabled={isUploading}
            color="primary"
          />
        </>
      )}
    </DocumentRow>
  );
}

// ----------------------------------------------------------------------
// MAIN PAGE
// ----------------------------------------------------------------------
export default function PageLegalDocs() {
  const { height } = useWindowDimensions();
  const { officialDocuments, templates, isAdminRole, isSuperUserRole, isPlannerAnalystRole } = usePrivacyContext();

  // Template Management: compatta di default, si espande con la freccia nell'header
  const [templatesExpanded, setTemplatesExpanded] = useState(false);
  const [isAddingDocument, setIsAddingDocument] = useState(false);

  // Planner/Analyst — Documents section: ToS + informativa semplificata + informativa estesa
  const plannerDocuments = officialDocuments.filter(
    (doc) => doc.type === 'general' || doc.type === 'informativa_semplificata' || doc.type === 'informativa_estesa'
  );

  // Una riga per tipo di template; la lingua si sceglie al click su download/upload
  const templateGroups = buildTemplateGroups(templates);

  const contentHeight =
    height -
    HEADER.DASHBOARD_DESKTOP_BANNER_HEIGHT -
    HEADER.DASHBOARD_DESKTOP_UNDER_BANNER_HEIGHT -
    70;

  return (
    <Page title="Legal Docs">
      <BoxTitlePage>
        <Typography variant="h2">Documents & Templates</Typography>
      </BoxTitlePage>
      <Box
        sx={{
          width: '100%',
          height: contentHeight,
        }}
      >
        <ScrollContainer
          sx={{
            '&::-webkit-scrollbar': { width: '14px' },
          }}
        >
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={3}>

              {/* ── DOCUMENTS ── */}
              <SectionCard>
                <SectionHeader>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                          lineHeight: 1.3,
                        }}
                      >
                        Documents
                      </Typography>
                      <Typography variant="caption" lineHeight={1.4} display="block" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                        {isSuperUserRole
                          ? 'Upload your completed LIA, DPIA and Privacy Notices, or add your own documents. Download official Blimp documents.'
                          : isAdminRole
                            ? 'Download and update the General Terms of Service.'
                            : 'Download the General Terms of Service and the Privacy Notices uploaded by your headquarters.'}
                      </Typography>
                    </Box>
                  </Stack>

                  {isSuperUserRole && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isAddingDocument}
                      onClick={() => setIsAddingDocument(true)}
                      sx={{
                        color: '#fff',
                        borderColor: 'rgba(255,255,255,0.6)',
                        textTransform: 'none',
                        borderRadius: 2,
                        flexShrink: 0,
                        '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.3)' },
                      }}
                    >
                      Add document
                    </Button>
                  )}
                </SectionHeader>

                {isPlannerAnalystRole
                  ? plannerDocuments.map((doc) => (
                    <PlannerDocumentRow key={doc.id} doc={doc} />
                  ))
                  : officialDocuments
                    .filter((doc) => isSuperUserRole || doc.type === 'general')
                    .map((doc) =>
                      isSuperUserRole
                        ? <SuperUserDocumentRow key={doc.id} doc={doc} />
                        : <AdminDocumentRow key={doc.id} doc={doc} />
                    )
                }

                {isSuperUserRole && isAddingDocument && (
                  <AddDocumentRow onClose={() => setIsAddingDocument(false)} />
                )}
              </SectionCard>

              {/* ── TEMPLATES — Admin / SuperUser only ── */}
              {!isPlannerAnalystRole && (
                <>
                  <Divider />
                  <SectionCard>
                    <SectionHeader>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              letterSpacing: '0.02em',
                              lineHeight: 1.3,
                            }}
                          >
                            Template Management
                          </Typography>
                          <Typography variant="caption" lineHeight={1.4} display="block" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                            {isSuperUserRole
                              ? 'Download compliance templates: LIA, DPIA and Privacy Notice templates.'
                              : 'Manage Privacy Notice templates. Download or update simplified and full versions.'}
                          </Typography>
                        </Box>
                      </Stack>

                      <Tooltip title={templatesExpanded ? 'Comprimi' : 'Espandi'}>
                        <IconButton
                          size="small"
                          onClick={() => setTemplatesExpanded((prev) => !prev)}
                          sx={{
                            color: '#fff',
                            transform: templatesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        >
                          <ExpandMoreIcon />
                        </IconButton>
                      </Tooltip>
                    </SectionHeader>

                    {templatesExpanded && templateGroups.map((group) => (
                      <AdminTemplateGroupRow
                        key={group.type}
                        group={group}
                        canUpload={isAdminRole && !isSuperUserRole}
                      />
                    ))}
                  </SectionCard>
                </>
              )}

            </Stack>
          </Box>
        </ScrollContainer>
      </Box>
    </Page>
  );
}